import { ref } from "vue";
import { sanityClient } from "@/sanity/client";

export interface Project {
  _id: string;
  title: string;
  subtitle: string;
  slug: string;
  thumbnailUrl: string;
}

export interface ProjectDetails extends Project {
  categories: string[];
  publishedAt: string;
  details: any[]; // Or a more specific type for block content
  contentBlocks: ContentBlock[];
  credits: string[];
}

export interface ContentBlock {
  title: string;
  media: (SanityImage | SanityVideo)[];
}

export interface SanityImage {
  _type: "image";
  asset: {
    _ref: string;
    _type: "reference";
  };
}

export interface SanityVideo {
  _type: "video";
  url: string;
  caption?: string;
}

export interface About {
  about: any[]; // Or a more specific type for block content
  contacts: string[];
  clients: CreditColumn[];
  slug: string;
}

export interface CreditColumn {
  columnTitle: string;
  names: string[];
}

function useSanity() {
  const isLoading = ref(false);

  async function getProjects(): Promise<Project[]> {
    const query = `*[_type == "project" && isHidden != true] | order(orderRank) {
    _id,
    thumbTitle,
    title,
    subtitle,
    publishedAt,
    categories,
    "slug": slug.current,
    "thumbnailUrl": thumbnail.asset->url
    }`;

    try {
      isLoading.value = true;

      return await sanityClient.fetch(query);
    } catch (err) {
      console.log("error fetching projects from sanity:", err);
      return [];
    } finally {
      isLoading.value = false;
    }
  }

  async function getProjectDetails(projectId: string): Promise<Project | null> {
    const query = `*[_type == "project" && slug.current == $slug][0] {
  ...,
  "slug": slug.current,
  thumbnail {
    asset->
  },
  contentBlocks[]{
    ...,
    media[]{
      ...,
      // Handles your existing galleryMedia type
      _type == 'galleryMedia' => {
        "finalAspectRatio": select(
          aspectRatio == 'original' => image.asset->metadata.dimensions.aspectRatio,
          aspectRatio
        ),
        "image": image {
          asset->
        }
      },
      // Correctly projects your customVideo type
      _type == 'customVideo' => {
        // First, grab all of your custom fields directly
        aspectRatio,
        thumbnailType,
        thumbnailTime,
        gifStartTime,
        gifEndTime,
        mp4Rendition,
        // Then, follow the reference for the 'video' field
        video {
          asset-> {
            playbackId,
            assetId,
            "staticRenditions": data.static_renditions.items[]{
              name,
              status,
              filesize
            },
            // Include other useful Mux data
            data,
            duration,
            status
          }
        }
      }
    }
  }
}`;

    const params = { slug: projectId };

    try {
      isLoading.value = true;
      // The fetched data will conform to the imported Project type
      const project: Project = await sanityClient.fetch(query, params);
      return project;
    } catch (err) {
      console.error(
        `Error fetching project details for slug "${projectId}":`,
        err
      );
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function getAbout(): Promise<About | null> {
    const query = `*[_type == "about" && slug.current == "about"][0] {
      about,
      contacts,
      clients[] {
        columnTitle,
        names
      },
      "slug": slug.current
    }`;

    try {
      isLoading.value = true;
      const aboutData: About = await sanityClient.fetch(query);
      return aboutData;
    } catch (err) {
      console.error("Error fetching about document:", err);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    getProjects,
    getProjectDetails,
    getAbout,
    isLoading,
  };
}

export default useSanity;
